"use client";

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  Assignment,
  AssignmentFormData,
  AssignmentStats,
  Restaurant,
  Supplier,
  ViewType,
} from "@/components/TenantsAssignmentParent/types";

// Assignment modal state
interface AssignmentModalState {
  open: boolean;
  prefilledData: Partial<AssignmentFormData> | null;
}

// Slice state interface
interface TenantsAssignmentState {
  // Current view
  currentView: ViewType;

  // Stats
  stats: AssignmentStats | null;
  statsLoading: boolean;

  // Restaurants (left column - By Restaurant view)
  restaurants: Restaurant[];
  restaurantsLoading: boolean;
  selectedRestaurantId: string | null;

  // Suppliers (left column - By Supplier view)
  suppliers: Supplier[];
  suppliersLoading: boolean;
  selectedSupplierId: string | null;

  // Assignments (center table)
  assignments: Assignment[];
  assignmentsLoading: boolean;

  // Available entities (right column)
  availableSuppliers: Supplier[];
  availableRestaurants: Restaurant[];

  // Assignment modal
  assignmentModal: AssignmentModalState;
  assignmentSubmitLoading: boolean;
}

// Initial state
const initialState: TenantsAssignmentState = {
  currentView: "by_restaurant",

  stats: null,
  statsLoading: false,

  restaurants: [],
  restaurantsLoading: false,
  selectedRestaurantId: null,

  suppliers: [],
  suppliersLoading: false,
  selectedSupplierId: null,

  assignments: [],
  assignmentsLoading: false,

  availableSuppliers: [],
  availableRestaurants: [],

  assignmentModal: {
    open: false,
    prefilledData: null,
  },
  assignmentSubmitLoading: false,
};

const tenantsAssignmentSlice = createSlice({
  name: "tenantsAssignment",
  initialState,
  reducers: {
    // View
    setCurrentView: (state, action: PayloadAction<ViewType>) => {
      state.currentView = action.payload;
      // Reset selections when switching views
      state.selectedRestaurantId = null;
      state.selectedSupplierId = null;
      state.assignments = [];
    },

    // Stats
    setStats: (state, action: PayloadAction<AssignmentStats>) => {
      state.stats = action.payload;
    },
    setStatsLoading: (state, action: PayloadAction<boolean>) => {
      state.statsLoading = action.payload;
    },

    // Restaurants
    setRestaurants: (state, action: PayloadAction<Restaurant[]>) => {
      state.restaurants = action.payload;
    },
    setRestaurantsLoading: (state, action: PayloadAction<boolean>) => {
      state.restaurantsLoading = action.payload;
    },
    setSelectedRestaurantId: (state, action: PayloadAction<string | null>) => {
      state.selectedRestaurantId = action.payload;
    },

    // Suppliers
    setSuppliers: (state, action: PayloadAction<Supplier[]>) => {
      state.suppliers = action.payload;
    },
    setSuppliersLoading: (state, action: PayloadAction<boolean>) => {
      state.suppliersLoading = action.payload;
    },
    setSelectedSupplierId: (state, action: PayloadAction<string | null>) => {
      state.selectedSupplierId = action.payload;
    },

    // Assignments
    setAssignments: (state, action: PayloadAction<Assignment[]>) => {
      state.assignments = action.payload;
    },
    setAssignmentsLoading: (state, action: PayloadAction<boolean>) => {
      state.assignmentsLoading = action.payload;
    },
    removeAssignment: (state, action: PayloadAction<string>) => {
      state.assignments = state.assignments.filter(
        (a) => a.id !== action.payload,
      );
    },

    // Available entities
    setAvailableSuppliers: (state, action: PayloadAction<Supplier[]>) => {
      state.availableSuppliers = action.payload;
    },
    setAvailableRestaurants: (state, action: PayloadAction<Restaurant[]>) => {
      state.availableRestaurants = action.payload;
    },

    // Assignment modal
    openAssignmentModal: (
      state,
      action: PayloadAction<Partial<AssignmentFormData> | null>,
    ) => {
      state.assignmentModal.open = true;
      state.assignmentModal.prefilledData = action.payload;
    },
    closeAssignmentModal: (state) => {
      state.assignmentModal.open = false;
      state.assignmentModal.prefilledData = null;
    },
    setAssignmentSubmitLoading: (state, action: PayloadAction<boolean>) => {
      state.assignmentSubmitLoading = action.payload;
    },

    // Reset state
    resetTenantsAssignmentState: () => initialState,
  },
});

export const {
  setCurrentView,
  setStats,
  setStatsLoading,
  setRestaurants,
  setRestaurantsLoading,
  setSelectedRestaurantId,
  setSuppliers,
  setSuppliersLoading,
  setSelectedSupplierId,
  setAssignments,
  setAssignmentsLoading,
  removeAssignment,
  setAvailableSuppliers,
  setAvailableRestaurants,
  openAssignmentModal,
  closeAssignmentModal,
  setAssignmentSubmitLoading,
  resetTenantsAssignmentState,
} = tenantsAssignmentSlice.actions;

export default tenantsAssignmentSlice.reducer;
